import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ArrowRight, FolderKanban, Pencil, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Projects() {
  const { projects, refreshRefs } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", key: "", description: "" });

  // Edit project state
  const [editOpen, setEditOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", key: "", description: "" });
  const [saving, setSaving] = useState(false);

  const submitCreate = async () => {
    try {
      await api.post("/projects", { ...form, project_type: "software", color: "indigo" });
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", key: "", description: "" });
      refreshRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create project");
    }
  };

  const openEdit = (p, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setEditingProject(p);
    setEditForm({
      name: p.name || "",
      key: p.key || "",
      description: p.description || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editingProject) return;
    if (!editForm.name.trim()) {
      toast.error("Project name cannot be empty");
      return;
    }
    if (!editForm.key.trim()) {
      toast.error("Project short name (key) cannot be empty");
      return;
    }

    setSaving(true);
    try {
      const res = await api.patch(`/projects/${editingProject.id}`, {
        name: editForm.name.trim(),
        key: editForm.key.trim().toUpperCase(),
        description: editForm.description.trim(),
      });
      toast.success(
        editingProject.key !== res.data.key
          ? `Project updated! All ticket IDs migrated to ${res.data.key}-*`
          : "Project updated successfully"
      );
      setEditOpen(false);
      setEditingProject(null);
      refreshRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1600px]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Workspace</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Projects</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-project-btn" className="gap-1.5">
          <Plus className="w-4 h-4" /> New project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-16 text-center">
          <FolderKanban className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-lg font-semibold mb-1">No projects yet</div>
          <div className="text-sm text-muted-foreground mb-4">Create your first project to start tracking work.</div>
          <Button onClick={() => setOpen(true)}>Create project</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => {
            const displayInitials = ((p.key || p.name || "PR").trim().slice(0, 2)).toUpperCase();
            return (
              <div key={p.id} className="relative group">
                <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.key || p.id}`}>
                  <Card className="p-5 h-full hover:border-primary/50 transition-colors flex flex-col justify-between">
                    <div>
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 grid place-items-center rounded-md bg-primary/15 text-primary font-mono font-semibold">
                          {displayInitials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-base font-semibold truncate">{p.name}</div>
                          <div className="text-xs font-mono text-muted-foreground">
                            {p.key ? (
                              <span className="text-primary font-semibold">{p.key}</span>
                            ) : (
                              <span className="text-amber-500 font-sans italic text-[11px]">(No short name set)</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-7 h-7 text-muted-foreground hover:text-foreground opacity-70 group-hover:opacity-100"
                            onClick={(e) => openEdit(p, e)}
                            title="Edit Project Name & Key"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">
                        {p.description || "No description"}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground pt-2 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-secondary uppercase tracking-wider">{p.project_type || "SOFTWARE"}</span>
                        <span>{p.members?.length || 1} members</span>
                      </div>
                      <span className="text-primary hover:underline text-xs">Open project &rarr;</span>
                    </div>
                  </Card>
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* New Project Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Project Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Surana SFCC"
                data-testid="np-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Short Name / Key (e.g. SJ, SUR, PRJ)</Label>
              <Input
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase() })}
                placeholder="e.g. SJ"
                maxLength={8}
                data-testid="np-key"
              />
              <p className="text-[11px] text-muted-foreground">Used as the prefix for all ticket IDs (e.g. SJ-1, SJ-2).</p>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Describe what this project is for…"
                data-testid="np-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} data-testid="np-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Pencil className="w-4 h-4 text-primary" /> Edit Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="space-y-1.5">
              <Label>Project Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="e.g. Surana SFCC"
                data-testid="edit-proj-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Short Name / Key (Prefix for Tickets)</Label>
              <Input
                value={editForm.key}
                onChange={(e) => setEditForm({ ...editForm, key: e.target.value.toUpperCase() })}
                placeholder="e.g. SJ"
                maxLength={8}
                data-testid="edit-proj-key"
              />
              <div className="p-2.5 rounded-md bg-primary/10 border border-primary/20 text-xs text-primary leading-relaxed">
                💡 <strong>Automatic Ticket Migration:</strong> Setting or changing the short name to <strong>{editForm.key || "KEY"}</strong> will automatically update all existing tickets in this project (e.g. <code>-44</code> or <code>OLD-44</code> &rarr; <code>{editForm.key || "KEY"}-44</code>).
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
                placeholder="Project description…"
                data-testid="edit-proj-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} data-testid="edit-proj-submit">
              {saving ? "Updating tickets…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
